import { normalizeCountryLabel } from "../../../../../../../../../../../_shared/_logic/countries"
import {
  buildPropertyObjectTagPresentation,
  propertyObjectIconForScope,
  type PropertyObjectTagPresentation,
} from "../../../../../../../../../_shared/_logic/object-tags"
import {
  formatPropertyCustomerRole,
  normalizePropertyCustomerRole,
} from "../../../../../../../../../_shared/_logic/customer-roles"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
  tooltipList,
} from "../../../../../../../../../../../_shared/_logic/rich-tooltips"
import type { CustomerObjectEntry } from "../customers-table-contract"

type VisiblePropertyLinkGroup = {
  id: string
  propertyId: string
  propertyUnitId: string | null
  links: PropertyCustomer[]
}

export const sortPropertyLinks = (links: PropertyCustomer[]) =>
  links.slice().sort((left, right) => {
    if (left.is_primary !== right.is_primary)
      return Number(right.is_primary) - Number(left.is_primary)

    if (left.property_id !== right.property_id)
      return left.property_id.localeCompare(right.property_id, "de")

    return (left.property_unit_id ?? "").localeCompare(right.property_unit_id ?? "", "de")
  })

export const normalizePropertyUnitId = (value: string | null | undefined) => value?.trim() || null

export const roleValuesForLinks = (links: PropertyCustomer[]) =>
  Array.from(
    new Set(sortPropertyLinks(links).map((link) => normalizePropertyCustomerRole(link.role))),
  )

export const roleTooltipForEntries = (
  entries: CustomerObjectEntry[],
  role: PropertyCustomerRole,
) => {
  const roleLabel = formatPropertyCustomerRole(role)
  const matchingEntries = entries
    .filter((entry) => entry.roleValues.includes(role))
    .map((entry) => entry.lines.filter(Boolean).join(", "))
    .filter(Boolean)

  if (!matchingEntries.length) return `${roleLabel}: keine konkrete Einheit zugeordnet.`

  return [
    `${roleLabel} gilt für:`,
    ...matchingEntries.slice(0, 3).map((entry) => `- ${entry}`),
    ...(matchingEntries.length > 3 ? [`… und ${matchingEntries.length - 3} weitere`] : []),
  ].join("\n")
}

export const buildVisiblePropertyLinkGroups = (
  links: PropertyCustomer[],
): VisiblePropertyLinkGroup[] => {
  const sortedLinks = sortPropertyLinks(links)
  const propertiesWithUnitLinks = new Set(
    sortedLinks
      .filter((link) => normalizePropertyUnitId(link.property_unit_id))
      .map((link) => link.property_id),
  )
  const groupsByDisplayKey = new Map<string, VisiblePropertyLinkGroup>()

  for (const link of sortedLinks) {
    const unitId = normalizePropertyUnitId(link.property_unit_id)

    if (!unitId && propertiesWithUnitLinks.has(link.property_id)) continue

    const displayKey = `${link.property_id}:${unitId ?? "__property"}`
    const existing = groupsByDisplayKey.get(displayKey)

    if (existing) {
      existing.links.push(link)
      continue
    }

    groupsByDisplayKey.set(displayKey, {
      id: link.id || displayKey,
      propertyId: link.property_id,
      propertyUnitId: unitId,
      links: [link],
    })
  }

  return Array.from(groupsByDisplayKey.values())
}

export const buildFallbackObjectEntries = (customer: CustomerListEntry): CustomerObjectEntry[] => {
  const addressParts = (customer.primary_address_label ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
  const fallbackAddress = addressParts[0] ?? "Keine Adresse hinterlegt"
  const tag: PropertyObjectTagPresentation = {
    label: "Gebäudeeinheit",
    subLabel: fallbackAddress,
    color: "violet",
    icon: propertyObjectIconForScope("BUILDING"),
    scope: "BUILDING",
    sortLabel: ["Gebäudeeinheit", fallbackAddress].filter(Boolean).join(" "),
  }
  const lines = [tag.label, ...(addressParts.length ? addressParts : [fallbackAddress])]

  return [
    {
      id: `${customer.id}-fallback-property`,
      lines,
      tag,
      roleValues: [],
      tooltip: buildRichTooltip([
        tooltipTitle(tag.label),
        ...lines.slice(1).map((line) => tooltipText(line)),
      ]),
      tooltipHtml: true,
    },
  ]
}

const formatCityLine = (property: Property | undefined) =>
  [property?.zip, property?.city].filter(Boolean).join(" ").trim()

export const buildObjectEntries = (
  customer: CustomerListEntry,
  linksByCustomerId: Record<string, PropertyCustomer[]>,
  propertiesById: Record<string, Property>,
): CustomerObjectEntry[] => {
  const groups = buildVisiblePropertyLinkGroups(linksByCustomerId[customer.id] ?? [])

  if (!groups.length) return buildFallbackObjectEntries(customer)

  return groups.map((group, index) => {
    const property = propertiesById[group.propertyId]
    const tag = buildPropertyObjectTagPresentation({
      property,
      propertyUnitId: group.propertyUnitId,
      mode: "unit-first",
    })
    const roleValues = roleValuesForLinks(group.links)
    const lines = [
      tag.label,
      tag.subLabel,
      formatCityLine(property),
      property ? normalizeCountryLabel(property.country) : null,
    ].filter((line): line is string => Boolean(line?.trim()))
    const normalizedLines = lines.length ? lines : [tag.label, "Keine Adresse hinterlegt"]
    const roleLabels = roleValues.map((role) => formatPropertyCustomerRole(role))

    return {
      id: group.id || `${customer.id}-property-${index}`,
      lines: normalizedLines,
      tag,
      roleValues,
      tooltip: buildRichTooltip([
        tooltipTitle(tag.label),
        ...normalizedLines.slice(1).map((line) => tooltipText(line)),
        tooltipField("Rollen", roleLabels.join(", ")),
      ]),
      tooltipHtml: true,
    }
  })
}
